/**
 * The Bearer credential for the hosted-rowboat data plane. BetterAuth's `jwt` plugin mints these at
 * `GET /api/auth/token` with `sub = user.id` and the `iss`/`aud` that `provision:*` registered with
 * rowboat, so the router's `resolveAuthor` verifies them against CheckList's public JWKS.
 *
 * One token is cached for the author this page acts as (`setSyncTokenAuthor`) and re-minted a
 * minute before it expires. Expiry is the token's lifetime (`exp - iat`) counted from when the
 * mint was sent, so a skewed device clock never makes a live token look expired or the reverse.
 * Requests sent through `withSyncToken` drop the token on a 401 and retry once with a fresh one.
 */

const REFRESH_MARGIN_MS = 60_000;

// Mirrors auth-client.ts: an explicit base in prod/Capacitor, the page origin in dev (Vite proxies
// /api to the backend), which is what a relative URL resolves to.
const TOKEN_URL = `${import.meta.env.VITE_AUTH_URL ?? ''}/api/auth/token`;

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

let author: string | null = null;
let cached: { token: string; expiresAtMs: number } | null = null;

function claimsOf(token: string): { sub: unknown; lifetimeMs: number } {
  const payload = token.split('.')[1];
  if (!payload) throw new Error('syncToken: malformed JWT (no payload segment)');
  const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  const { sub, exp, iat } = JSON.parse(json) as { sub?: unknown; exp?: unknown; iat?: unknown };
  if (typeof exp !== 'number' || typeof iat !== 'number') {
    throw new Error('syncToken: JWT carries no numeric exp and iat claims');
  }
  return { sub, lifetimeMs: (exp - iat) * 1000 };
}

/**
 * Binds the cache to the account this page acts as. A token minted for another account (the
 * session cookie changed under this tab) is refused rather than used.
 */
export function setSyncTokenAuthor(next: string | null): void {
  if (next !== author) cached = null;
  author = next;
}

export async function getSyncToken(): Promise<string> {
  if (cached && Date.now() < cached.expiresAtMs - REFRESH_MARGIN_MS) return cached.token;

  const expected = author;
  const sentAt = Date.now();
  // Same-origin to CheckList, so the session cookie authenticates the mint.
  const res = await fetch(TOKEN_URL, { credentials: 'include' });
  if (!res.ok) throw new Error(`syncToken: GET /api/auth/token failed (${res.status})`);

  const { token } = (await res.json()) as { token?: unknown };
  if (typeof token !== 'string' || token.length === 0) {
    throw new Error('syncToken: response missing a token');
  }

  const { sub, lifetimeMs } = claimsOf(token);
  if (author !== expected) throw new Error('syncToken: the identity changed during the mint');
  if (expected !== null && sub !== expected) {
    throw new Error('syncToken: the session belongs to another account');
  }
  cached = { token, expiresAtMs: sentAt + lifetimeMs };
  return token;
}

/** Drops the cached token so the next call re-mints. */
export function clearSyncToken(): void {
  cached = null;
}

/**
 * `fetchFn` with the sync token as its Bearer. A request that already carries an Authorization
 * header, such as a signed blob read, passes through untouched.
 */
export function withSyncToken(fetchFn: FetchFn): FetchFn {
  return async (input, init) => {
    if (new Headers(init?.headers).has('authorization')) return fetchFn(input, init);
    const send = async () => {
      const token = await getSyncToken();
      const headers = new Headers(init?.headers);
      headers.set('authorization', `Bearer ${token}`);
      return { token, res: await fetchFn(input, { ...init, headers }) };
    };
    const first = await send();
    if (first.res.status !== 401) return first.res;
    // Only the token that was refused: another request may already have cached a fresh one.
    if (cached?.token === first.token) cached = null;
    return (await send()).res;
  };
}
